import React, { useEffect, useRef, useState } from "react";
import MetricToggle from "./MetricToggle";
import HeaderTabSelector from "./HeaderTabSelector";
import CompliancePanel from "./CompliancePanel";
import AssetDocumentsPanel from "./AssetDocumentsPanel";

export default function AssetEditPopup(props) {

  var open = props.open;
  var asset = props.asset;               // existing asset object (or null for new)
  var contextText = props.contextText;   // e.g. "Jane Smith • HQ Building • Floor 1 • Desk RF.08.M12.06"
  var onClose = props.onClose;
  var onSave = props.onSave;

  var dropRef = useRef(null);

  var [draft, setDraft] = useState({
    id: null,
    type: "Monitor",
    assetClass: "IT",
    name: "",
    manufacturer: "",
    model: "",
    serial: "",
    assetTag: "",
    condition: "Good",
    criticality: "Medium",
    rigidity: "Team-owned",
    comments: ""
  });
  
  var [condition, setCondition] = useState("Good");
  var [criticality, setCriticality] = useState("Medium");
  var [rigidity, setRigidity] = useState("Moderate");
  
  var [photos, setPhotos] = useState([]); // [{ id, url, file, name }]
  
  const [activeTab, setActiveTab] = useState('asset');
 

  useEffect(function () {

    if (!open) {
      return;
    }

    // Reset to asset when opened
    if (asset) {
      setDraft({
        id: asset.id || null,
        type: asset.type || "Monitor",
        assetClass: asset.assetClass || "IT",
        name: asset.name || "",
        manufacturer: asset.manufacturer || "",
        model: asset.model || "",
        serial: asset.serial || "",
        assetTag: asset.assetTag || "",
        condition: asset.condition || "Good",
        criticality: asset.criticality || "Medium",
        rigidity: asset.rigidity || "Team-owned",
        comments: asset.comments || ""
      });

      // If you later store photo URLs on the asset, you can hydrate them here.
      setPhotos([]);
      return;
    }

    // New asset defaults
    setDraft({
      id: null,
      type: "Monitor",
      assetClass: "IT",
      name: "",
      manufacturer: "",
      model: "",
      serial: "",
      assetTag: "",
      condition: "Good",
      criticality: "Medium",
      rigidity: "Team-owned",
      comments: ""
    });
    setPhotos([]);

  }, [open, asset]);

  function setField(name, value) {
    setDraft(function (prev) {
      var next = Object.assign({}, prev);
      next[name] = value;
      return next;
    });
  }

  function addFiles(fileList) {

    if (!fileList || fileList.length === 0) {
      return;
    }

    var arr = Array.prototype.slice.call(fileList);

    setPhotos(function (prev) {

      var next = prev.slice(0);

      arr.forEach(function (file) {

        var url = URL.createObjectURL(file);

        next.push({
          id: "ph-" + Date.now() + "-" + Math.floor(Math.random() * 100000),
          url: url,
          file: file,
          name: file.name
        });
      });

      return next;
    });
  }

  function removePhoto(id) {

    setPhotos(function (prev) {

      var next = [];

      prev.forEach(function (p) {
        if (p.id !== id) {
          next.push(p);
        } else {
          try { URL.revokeObjectURL(p.url); } catch (e) {}
        }
      });

      return next;
    });
  }

  function onDrop(e) {

    e.preventDefault();
    e.stopPropagation();

    if (dropRef.current) {
      dropRef.current.style.borderColor = "rgba(0,0,0,0.12)";
      dropRef.current.style.background = "#ffffff";
    }

    var dt = e.dataTransfer;
    if (dt && dt.files) {
      addFiles(dt.files);
    }
  }

  function onDragOver(e) {
    e.preventDefault();
    e.stopPropagation();
    if (dropRef.current) {
      dropRef.current.style.borderColor = "rgba(31,111,229,0.55)";
      dropRef.current.style.background = "rgba(31,111,229,0.06)";
    }
  }

  function onDragLeave(e) {
    e.preventDefault();
    e.stopPropagation();
    if (dropRef.current) {
      dropRef.current.style.borderColor = "rgba(0,0,0,0.12)";
      dropRef.current.style.background = "#ffffff";
    }
  }

  function handleSave() {

    if (!draft.name || String(draft.name).trim().length === 0) {
      alert("Give the asset a name (e.g. Dell 27\" 4K).");
      return;
    }

    // Pass files up so you can upload later.
    var result = Object.assign({}, draft);
    result.photos = photos;

    if (onSave) {
      onSave(result);
    }
  }

  if (!open) {
    return null;
  }

  var conditionOptions = [
    { key: "Excellent", label: "Excellent", hint: "Like new" },
    { key: "Good",      label: "Good",      hint: "Normal wear" },
    { key: "Worn",      label: "Worn",      hint: "Cosmetic / tired" },
    { key: "End of Life", label: "End of Life", hint: "Replace soon" }
  ];

  var criticalityOptions = [
    { key: "Low", label: "Low", hint: "Nice-to-have" },
    { key: "Medium", label: "Medium", hint: "Productivity impact" },
    { key: "High", label: "High", hint: "Operationally critical" },
    { key: "Mission Critical", label: "Mission Critical", hint: "Cannot operate" }
  ];

  var rigidityOptions = [
    { key: "Hot-deskable", label: "Hot-deskable" },
    { key: "Shared pool", label: "Shared pool" },
    { key: "Team-owned", label: "Team-owned" },
    { key: "Person-bound", label: "Person-bound" },
    { key: "Fixed / Installed", label: "Fixed / Installed" }
  ];

  var styles = {
	
	metricRow: {
	  display: "flex",
	  gap: "16px",
	  flexWrap: "nowrap",
	  marginLeft:'20px',
	  marginRight:'20px',
	  marginTop:'10px',
	  width:'calc(100% - 40px)'
	},

    backdrop: {
      position: "fixed",
      inset: 0,
      background: "rgba(0,0,0,0.45)",
      zIndex: 5000
    },

    popup: {
      position: "absolute",
      left: "5%",
      right: "5%",
      top: "90px",
      bottom: "20px",
      background: "linear-gradient(180deg, #FFFFFF 0%, #F3F5F8 100%)",
      borderRadius: "12px",
      boxShadow: "0 18px 70px rgba(0,0,0,0.35)",
      overflow: "hidden",
      display: "flex",
      flexDirection: "column"
    },

    header: {
      padding: "16px 18px",
      borderBottom: "1px solid rgba(0,0,0,0.08)",
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      background: "linear-gradient(180deg, #FFFFFF 0%, #EEF1F6 100%)"
    },

    headerLeft: {
      display: "flex",
      flexDirection: "column"
    },

    title: {
      fontSize: "18px",
      fontWeight: 700,
      color: "#111"
    },

    sub: {
      fontSize: "12px",
      color: "rgba(0,0,0,0.60)",
      marginTop: "2px"
    },

    closeBtn: {
      width: "36px",
      height: "36px",
      borderRadius: "10px",
      border: "1px solid rgba(0,0,0,0.10)",
      background: "#fff",
      cursor: "pointer",
      fontSize: "18px",
      lineHeight: "34px"
    },

    body: {
      flex: 1,
      display: "flex",
      gap: "14px",
      padding: "14px",
      overflow: "hidden",
      minHeight: 0
    },

    col: {
      background: "#fff",
      borderRadius: "12px",
      border: "1px solid rgba(0,0,0,0.08)",
      boxShadow: "0 10px 28px rgba(0,0,0,0.08)",
      overflow: "hidden",
      display: "flex",
      flexDirection: "column",
      minWidth: 0,
      minHeight: 0,
	  margin:'10px'
    },

    colLeft: { flex: 1.05 },
    colRight: { flex: 1.15 , flexDirection:'column'},

    colHeader: {
      padding: "12px 14px",
      borderBottom: "1px solid rgba(0,0,0,0.06)",
      fontWeight: 700,
      fontSize: "13px",
	  background: 'linear-gradient(180deg, rgba(66,133,244,1) 0%, rgba(30,88,182,1) 100%)',
	  color: '#fff'
    },

    section: {
      padding: "12px 14px",
      borderBottom: "1px solid rgba(0,0,0,0.06)"
    },

    sectionTitle: {
      fontSize: "12px",
      fontWeight: 700,
      color: "rgba(0,0,0,0.70)",
      marginBottom: "10px"
    },

    grid2: {
      display: "grid",
      gridTemplateColumns: "1fr 1fr",
      gap: "10px"
    },

    field: {
      display: "flex",
      flexDirection: "column"
    },

    label: {
      fontSize: "11px",
      color: "rgba(0,0,0,0.60)",
      marginBottom: "6px",
      fontWeight: 600
    },

    input: {
      padding: "10px 10px",
      borderRadius: "10px",
      border: "1px solid rgba(0,0,0,0.12)",
      outline: "none",
      fontSize: "13px",
      background: "#fff"
    },

    select: {
      padding: "10px 10px",
      borderRadius: "10px",
      border: "1px solid rgba(0,0,0,0.12)",
      outline: "none",
      fontSize: "13px",
      background: "#fff"
    },

    optionRow: {
      display: "grid",
      gridTemplateColumns: "1fr 1fr",
      gap: "10px"
    },

    card: {
      border: "1px solid rgba(0,0,0,0.10)",
      borderRadius: "12px",
      padding: "10px 10px",
      cursor: "pointer",
      background: "#fff"
    },

    cardActive: {
      border: "1px solid rgba(31,111,229,0.55)",
      background: "rgba(31,111,229,0.06)",
      boxShadow: "0 8px 18px rgba(31,111,229,0.12)"
    },

    cardTop: {
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between"
    },

    cardLabel: {
      fontWeight: 700,
      fontSize: "13px",
      color: "#111"
    },

    cardHint: {
      fontSize: "11px",
      color: "rgba(0,0,0,0.55)",
      marginTop: "4px"
    },

    pillRow: {
      display: "flex",
      flexWrap: "wrap",
      gap: "8px"
    },

    pill: {
      border: "1px solid rgba(0,0,0,0.12)",
      background: "#fff",
      padding: "8px 10px",
      borderRadius: "999px",
      cursor: "pointer",
      fontSize: "12px",
      fontWeight: 700,
      color: "rgba(0,0,0,0.70)"
    },

    pillActive: {
      border: "1px solid rgba(31,111,229,0.55)",
      background: "linear-gradient(90deg, rgba(31,111,229,0.14), rgba(42,141,247,0.14))",
      color: "#1f6fe5"
    },

    drop: {
      border: "1px dashed rgba(0,0,0,0.12)",
      borderRadius: "12px",
      padding: "12px",
      background: "#fff"
    },

    dropTop: {
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      marginBottom: "10px"
    },

    dropText: {
      fontSize: "12px",
      color: "rgba(0,0,0,0.65)",
      fontWeight: 600
    },

    uploadBtn: {
      border: "1px solid rgba(0,0,0,0.12)",
      background: "#fff",
      borderRadius: "10px",
      padding: "8px 10px",
      cursor: "pointer",
      fontWeight: 700,
      fontSize: "12px"
    },

    thumbGrid: {
      display: "grid",
      gridTemplateColumns: "repeat(4, 1fr)",
      gap: "10px"
    },

    thumb: {
      position: "relative",
      borderRadius: "12px",
      overflow: "hidden",
      border: "1px solid rgba(0,0,0,0.10)",
      height: "74px",
      background: "#f3f5f8"
    },

    thumbImg: {
      width: "100%",
      height: "100%",
      objectFit: "cover"
    },

    thumbX: {
      position: "absolute",
      top: "6px",
      right: "6px",
      width: "22px",
      height: "22px",
      borderRadius: "8px",
      border: "1px solid rgba(0,0,0,0.10)",
      background: "rgba(255,255,255,0.95)",
      cursor: "pointer",
      fontWeight: 900,
      lineHeight: "20px",
      textAlign: "center"
    },

    textarea: {
      width: "100%",
      minHeight: "110px",
      resize: "vertical",
      padding: "10px 10px",
      borderRadius: "12px",
      border: "1px solid rgba(0,0,0,0.12)",
      outline: "none",
      fontSize: "13px"
    },

    footer: {
      padding: "14px 18px",
      borderTop: "1px solid rgba(0,0,0,0.08)",
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      background: "#fff"
    },

    footLeft: {
      fontSize: "12px",
      color: "rgba(0,0,0,0.60)"
    },

    btnRow: {
      display: "flex",
      gap: "10px",
      alignItems: "center"
    },

    btnCancel: {
      border: "1px solid rgba(0,0,0,0.16)",
      background: "#fff",
      borderRadius: "10px",
      padding: "10px 14px",
      cursor: "pointer",
      fontWeight: 700
    },

    btnSave: {
      border: "none",
      background: "linear-gradient(90deg, #1f6fe5, #2a8df7)",
      color: "#fff",
      borderRadius: "10px",
      padding: "10px 16px",
      cursor: "pointer",
      fontWeight: 800,
      boxShadow: "0 12px 26px rgba(31,111,229,0.25)"
    },
	
	modalBody: {
	    position: "relative",
	    overflow: "hidden",
	    flex: 1,
	    background: "#f5f7fa"
	  },

	  slider: {
	    display: "flex",
	    width: "300%",
	    height: "100%",
	    transition: "transform 0.35s ease"
	  },

	  panel: {
	    width: "33.33%",
	    padding: "10px",
	    overflowY: "auto"
	  },
	  
	  colWrap: {
		display:'flex',
		flexDirection:'row'
	  }
  };

  return (
    <div style={styles.backdrop}>
      <div style={styles.popup}>

        {/* HEADER */}
        <div style={styles.header}>
          <div style={styles.headerLeft}>
            <div style={styles.title}>Asset Profile</div>
            <div style={styles.sub}>
              {contextText || "Assign details, photos and intelligence fields"}
            </div>
          </div>

          <div style={styles.headerCenter}>
            <HeaderTabSelector
              tabs={[
                { key: "asset", label: "Asset" },
                { key: "compliance", label: "Compliance" },
                { key: "documents", label: "Documents" }
              ]}
              activeTab={activeTab}
              onChange={setActiveTab}
            />
          </div>

          <button style={styles.closeBtn} onClick={onClose}>✕</button>
        </div>

        {/* BODY */}
        <div style={styles.modalBody}>
		<div
		  style={{
		    ...styles.slider,
		    transform:
		      activeTab === "asset"
		        ? "translateX(0%)"
		        : activeTab === "compliance"
		        ? "translateX(-33.3333%)"
		        : "translateX(-66.6666%)"
		  }}
		>

            {/* ================= ASSET PANEL ================= */}
            <div style={styles.panel}>
              <div style={styles.colWrap}>

                {/* LEFT */}
                <div style={{ ...styles.col, ...styles.colLeft }}>
                  <div style={styles.colHeader}>Identity</div>

				  {/* ================= IDENTITY ================= */}
				  <div style={styles.section}>
				    <div style={styles.sectionTitle}>Classification</div>

				    <div style={styles.grid2}>
				      <div style={styles.field}>
				        <div style={styles.label}>Asset Type</div>
				        <select
				          style={styles.select}
				          value={draft.type}
				          onChange={function (e) {
				            setField("type", e.target.value);
				          }}
				        >
				          <option value="Monitor">Monitor</option>
				          <option value="Laptop">Laptop</option>
				          <option value="Keyboard">Keyboard</option>
				          <option value="Headset">Headset</option>
				          <option value="Phone">Phone</option>
				          <option value="Dock">Dock</option>
				          <option value="Chair">Chair</option>
				          <option value="Other">Other</option>
				        </select>
				      </div>

				      <div style={styles.field}>
				        <div style={styles.label}>Asset Class</div>
				        <select
				          style={styles.select}
				          value={draft.assetClass}
				          onChange={function (e) {
				            setField("assetClass", e.target.value);
				          }}
				        >
				          <option value="IT">IT</option>
				          <option value="Facilities">Facilities</option>
				          <option value="Clinical">Clinical</option>
				          <option value="Specialist">Specialist</option>
				        </select>
				      </div>
				    </div>
				  </div>

				  <div style={styles.section}>
				    <div style={styles.sectionTitle}>Asset Details</div>

				    <div style={styles.grid2}>
				      <div style={styles.field}>
				        <div style={styles.label}>Name</div>
				        <input
				          style={styles.input}
				          value={draft.name}
				          onChange={function (e) {
				            setField("name", e.target.value);
				          }}
				          placeholder='e.g. Dell 27" 4K'
				        />
				      </div>

				      <div style={styles.field}>
				        <div style={styles.label}>Manufacturer</div>
				        <input
				          style={styles.input}
				          value={draft.manufacturer}
				          onChange={function (e) {
				            setField("manufacturer", e.target.value);
				          }}
				          placeholder="e.g. Dell"
				        />
				      </div>

				      <div style={styles.field}>
				        <div style={styles.label}>Model</div>
				        <input
				          style={styles.input}
				          value={draft.model}
				          onChange={function (e) {
				            setField("model", e.target.value);
				          }}
				          placeholder="e.g. U2720Q"
				        />
				      </div>

				      <div style={styles.field}>
				        <div style={styles.label}>Serial Number</div>
				        <input
				          style={styles.input}
				          value={draft.serial}
				          onChange={function (e) {
				            setField("serial", e.target.value);
				          }}
				          placeholder="e.g. U2720Q-1120"
				        />
				      </div>

				      <div style={styles.field}>
				        <div style={styles.label}>Asset Tag</div>
				        <input
				          style={styles.input}
				          value={draft.assetTag}
				          onChange={function (e) {
				            setField("assetTag", e.target.value);
				          }}
				          placeholder="optional"
				        />
				      </div>
				    </div>
				  </div>

				  <div style={styles.section}>
				    <div style={styles.sectionTitle}>Photos</div>

				    <div
				      ref={dropRef}
				      style={styles.drop}
				      onDrop={onDrop}
				      onDragOver={onDragOver}
				      onDragLeave={onDragLeave}
				    >
				      <div style={styles.dropTop}>
				        <div style={styles.dropText}>
				          Drag & drop photos here (or upload)
				        </div>

				        <label style={styles.uploadBtn}>
				          Upload
				          <input
				            type="file"
				            multiple
				            accept="image/*"
				            style={{ display: "none" }}
				            onChange={function (e) {
				              addFiles(e.target.files);
				              e.target.value = "";
				            }}
				          />
				        </label>
				      </div>

				      {photos.length === 0 && (
				        <div style={{ fontSize: "12px", color: "rgba(0,0,0,0.45)" }}>
				          No photos added yet
				        </div>
				      )}

				      {photos.length > 0 && (
				        <div style={styles.thumbGrid}>
				          {photos.map(function (p) {
				            return (
				              <div key={p.id} style={styles.thumb} title={p.name}>
				                <img src={p.url} alt="" style={styles.thumbImg} />
				                <div
				                  style={styles.thumbX}
				                  onClick={function () {
				                    removePhoto(p.id);
				                  }}
				                >
				                  ✕
				                </div>
				              </div>
				            );
				          })}
				        </div>
				      )}
				    </div>
				  </div>

                </div>

                {/* RIGHT */}
                <div style={{ ...styles.col, ...styles.colRight }}>
                  <div style={styles.colHeader}>Intelligence</div>

                  <div style={styles.metricRow}>
                    <MetricToggle
                      label="Condition"
                      value={condition}
                      options={["Poor", "Fair", "Good"]}
                      onChange={setCondition}
                    />
                  
                    <MetricToggle
                      label="Criticality"
                      value={criticality}
                      options={["Low", "Medium", "High"]}
                      onChange={setCriticality}
                    />
                  
                    <MetricToggle
                      label="Rigidity"
                      value={rigidity}
                      options={["Flexible", "Moderate", "Fixed"]}
                      onChange={setRigidity}
                    />
                  </div>

                  <div style={styles.section}>
                    <div style={styles.sectionTitle}>Comments</div>
                    <textarea
                      style={styles.textarea}
                      value={draft.comments}
                      onChange={e => setField("comments", e.target.value)}
                      placeholder="Notes, quirks, known issues, special handling…"
                    />
                  </div>
                </div>

              </div>
            </div>

            {/* ================= COMPLIANCE PANEL ================= */}
            <div style={styles.panel}>
              <div style={{width:'100%', height:'100%', backgroundColor:'black', color:'white'}}>
			  	<CompliancePanel />
			  </div>
            </div>
			
			{/* ================= DOCUMENTS PANEL ================= */}
            <div style={styles.panel}>
              <div style={{width:'100%', height:'100%', backgroundColor:'white', color:'black'}}>
			  	<AssetDocumentsPanel />
			  </div>
            </div>

          </div>
        </div>

        {/* FOOTER */}
        <div style={styles.footer}>
          <div style={styles.footLeft}>
            Changes are local until you hit Save
          </div>

          <div style={styles.btnRow}>
            <button style={styles.btnCancel} onClick={onClose}>
              Cancel
            </button>
            <button style={styles.btnSave} onClick={handleSave}>
              Save Asset
            </button>
          </div>
        </div>

      </div>
    </div>
  );

}
